import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import Bowser from "bowser";
import { useMemo } from "react";

import { Canvas } from "@/components/canvas";
import { useIsMobile } from "@/hooks/use-mobile";
import { useShareQuery } from "@/queries/share";

export const Route = createFileRoute("/share/$code")({
	component: RouteComponent,
});

function isMobileUserAgent(): boolean {
	if (typeof navigator === "undefined") return false;

	// Chromium 계열: userAgentData.mobile 지원
	const anyNav = navigator as unknown as { userAgentData?: { mobile?: boolean } };
	if (typeof anyNav.userAgentData?.mobile === "boolean") {
		return anyNav.userAgentData.mobile;
	}

	const ua = navigator.userAgent ?? "";
	// 카카오톡 인앱 브라우저 등 일부 UA는 파서가 platform.type을 제대로 못 잡는 경우가 있어 보조 규칙 추가
	if (/KAKAOTALK|KakaoTalk/i.test(ua)) return true;

	const { platform, os } = Bowser.parse(ua);
	if (platform.type === "mobile" || platform.type === "tablet") return true;
	if (os.name === "Android" || os.name === "iOS") return true;

	return false;
}

function selectSlidesForDevice(slides: ISlide[], isMobileUA: boolean): ISlide[] {
	if (!isMobileUA) return slides;

	return slides.flatMap((slide) => {
		const mobileList = slide.mobile ?? [];
		if (mobileList.length === 0) return slide;

		// 스펙상 seq 오름차순이지만, 혹시라도 정렬이 깨져 들어오면 방어적으로 정렬
		const sortedMobile = [...mobileList].sort((a, b) => a.seq - b.seq);

		// 데스크탑 1 슬라이드 -> 모바일 N 슬라이드(1:n)
		return sortedMobile.map((m) => ({
			...slide,
			// Canvas는 배열 순서로 렌더링하므로, seq/id는 유니크/정렬 보조용으로만 유지
			seq: slide.seq * 1000 + m.seq,
			id: `${slide.id}__m${m.seq}`,
			type: m.type,
			data: m.data,
			// 이미 모바일로 펼친 슬라이드에서는 추가 mobile 정보가 불필요하므로 비움
			mobile: [],
		}));
	});
}

function RouteComponent() {
	const { code } = Route.useParams();
	const shareQuery = useShareQuery(code);
	const { data: shareData, isLoading, error } = useQuery(shareQuery);
	const isMobileViewport = useIsMobile();
	const isMobileUA = useMemo(() => isMobileUserAgent(), []);
	const shouldUseMobileSlides = isMobileUA || isMobileViewport;

	if (isLoading) {
		return (
			<div className="w-full h-screen flex items-center justify-center">
				<div className="text-lg">로딩 중...</div>
			</div>
		);
	}

	if (error || !shareData) {
		return (
			<div className="w-full h-screen flex items-center justify-center">
				<div className="text-lg text-red-600">
					공유 링크를 찾을 수 없습니다.
				</div>
			</div>
		);
	}

	const slidesForRender = selectSlidesForDevice(
		shareData.slides,
		shouldUseMobileSlides,
	);

	return (
		<div className="w-full h-screen">
			<Canvas
				slides={slidesForRender}
				isShared={true}
				source_type={shareData.type}
			/>
		</div>
	);
}
